import type { Virtualizer } from "@tanstack/react-virtual";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
    COLUMN_LIST_PADDING_BOTTOM,
    COLUMN_LIST_PADDING_TOP,
    COLUMN_ROW_HEIGHT,
} from "app/pages/sales-intelligence/common-components/column-picker/constants";

const OVERSCAN = 5;

const getRowHeight = () => COLUMN_ROW_HEIGHT;

/**
 * `measureElement` is removed from the surface deliberately. Rows are positioned by
 * `transform: translateY(virtualItem.start)`, and those offsets only stay constant while scrolling
 * because every row is the same fixed height. Dynamic measurement would rewrite the size cache
 * mid-scroll, changing every offset, and the rows would visibly lag the scrollbar.
 */
export type UniformRowVirtualizer = Omit<Virtualizer<Element, Element>, "measureElement" | "measure" | "resizeItem">;

type UniformRowVirtualizerParams = {
    count: number;
    getScrollElement: () => Element | null;
    /** Must be referentially stable — it is an input to the virtualiser's measurement cache. */
    getItemKey: (index: number) => string;
};

/**
 * The paddings are declared to the virtualiser rather than rendered inside the scroll container, so
 * that `virtualItem.start` and the container's real `scrollTop` share one coordinate space. Anything
 * that adds height inside the scroll element without being declared here puts them back out of phase.
 */
export const useUniformRowVirtualizer = ({
    count,
    getScrollElement,
    getItemKey,
}: UniformRowVirtualizerParams): UniformRowVirtualizer =>
    // oxlint-disable-next-line react/incompatible-library -- @tanstack/react-virtual's re-render model is incompatible with memoization; no compiler transform is enabled in this repo, so this is safe
    useVirtualizer({
        count,
        getScrollElement,
        getItemKey,
        estimateSize: getRowHeight,
        overscan: OVERSCAN,
        paddingStart: COLUMN_LIST_PADDING_TOP,
        paddingEnd: COLUMN_LIST_PADDING_BOTTOM,
    });
